import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';

import { getSignatures } from './graphql/querys';
import { downloadFile } from '../../services/download';
import useAlert from 'utils/alert';
import { useEffect, useMemo, useState } from 'react';
import debounce from 'debounce';
import { getTokens } from 'utils/search';
import { filterSignatues } from './utils/filterSignatrues';

type Params = {
  query: string
}

export const useSignatures = ({ query }: Params) => {
  const { t } = useTranslation();
  const { id: orderId } = useParams<{id:string}>();
  const showAlert = useAlert();
  const [debouncedQuery, setDebouncedQuery] = useState(query);

  const signedDocuments = useQuery(
    ['getSignatures', orderId],
    () => getSignatures(orderId),
    {
      refetchOnWindowFocus: false
    }
  );

  useEffect(() => {
    const debounceSearch = debounce(() => {
      setDebouncedQuery(query);
    }, 1000);

    debounceSearch();

    return () => debounceSearch.clear();
  }, [query]);

  const signatures = useMemo(() => {
    const currentSignatures = signedDocuments.data?.getSignatures || [];

    if (debouncedQuery.length <= 0) {
      return [...currentSignatures]
        .sort((signatureA, signatureB) => signatureB.timestamp - signatureA.timestamp);
    }

    const { queryTokens } = getTokens({
      query: debouncedQuery,
      party: "",
      property: ""
    });

    return [...filterSignatues({ signatures: currentSignatures, queryTokens: queryTokens ?? [] })]
      .sort((signatureA, signatureB) => signatureB.timestamp - signatureA.timestamp);
  }, [debouncedQuery, signedDocuments.data]);

  const handleDownload = async (link: string, fileName: string) => {
    const success = await downloadFile(link, fileName);
    if (success) {
      showAlert(t('documents:document-download-success'), 'success');
    } else {
      showAlert(t('documents:error-downloading'), 'error');
    }
  };

  return {
    t,
    signatures,
    handleDownload
  };
};
